/**
 * Hook for managing filter cache in the Users tab
 *
 * This hook provides functionality to cache filter data for improved performance.
 * It manages storing, retrieving, and initializing filter data in the Redux store.
 *
 * Key features:
 * - Caching filter options to reduce API calls
 * - Time-based cache invalidation
 * - Batch initialization of multiple filters
 * - Memory-efficient storage of filter options
 *
 * @file use-filtercache.ts
 * @module Users/hooks
 */
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { type ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { type MenuOption } from '@saviynt/design-system';

import { type FilterCacheState } from '../../../../../ViolationsEntitlement/data-layer/entitlementTypes';

const useFilterCache = <U extends FilterCacheState>(
  filterCache: U,
  cacheFn: ActionCreatorWithPayload<U>
) => {
  const dispatch = useDispatch();

  const getFromCache = useCallback(
    <T extends string>(key: T, searchKey: T, cacheTime = 300000) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const recordsArr = filterCache.ids?.[key]?.[searchKey] ?? [];
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const lastFetchedTime = filterCache.metadata?.[key]?.lastfetch ?? 0;

      if (
        Array.isArray(recordsArr) &&
        recordsArr.length > 0 &&
        Date.now() - lastFetchedTime < cacheTime
      ) {
        return recordsArr.map((id: string) => filterCache.entries[key][id]);
      }

      return false;
    },
    [filterCache]
  );

  const setCache = useCallback(
    <T extends string>(key: T, searchKey: T, responseArr: MenuOption[]) => {
      const currentTimeStamp = Date.now();
      const newEntries = responseArr.reduce<Record<string, MenuOption>>(
        (acc, response) => {
          acc[response.value] = response;

          return acc;
        },
        {}
      );
      const newIds = responseArr.map((response) => response.value);
      const cacheObj = {
        ...filterCache,
        entries: {
          ...filterCache.entries,
          [key]: {
            ...filterCache.entries[key],
            ...newEntries, // Merge new entries into existing cache
          },
        },
        ids: {
          ...filterCache.ids,
          [key]: {
            ...filterCache.ids[key],
            [searchKey]: newIds, // Store user IDs for lookup
          },
        },
        metadata: {
          ...filterCache.metadata,
          [key]: { lastfetch: currentTimeStamp },
        },
      };

      dispatch(cacheFn(cacheObj));
    },
    [filterCache, dispatch, cacheFn]
  );

  const setGroupInitialCache = useCallback(
    <T extends string>(collection: Record<T, MenuOption[]>, searchKey: T) => {
      const currentTimeStamp = Date.now();
      const filterKeys = Object.keys(collection) as T[];

      const entries: FilterCacheState['entries'] = {};
      const ids: FilterCacheState['ids'] = {};
      const metadata: FilterCacheState['metadata'] = {};

      filterKeys.forEach((filterKey) => {
        const responses = collection[filterKey];

        entries[filterKey] = responses.reduce<Record<string, MenuOption>>(
          (acc, response) => {
            acc[response.value] = response;

            return acc;
          },
          {}
        );
        ids[filterKey] = {
          [searchKey]: responses.map((response) => response.value),
        };
        metadata[filterKey] = { lastfetch: currentTimeStamp };
      });

      const cacheObj: FilterCacheState = {
        entries,
        ids,
        metadata,
      };

      dispatch(cacheFn(cacheObj as U));
    },
    [dispatch, cacheFn]
  );

  return { getFromCache, setCache, setGroupInitialCache };
};

export default useFilterCache;
